/**
 * Progressive image loading, optimised for PERCEIVED speed.
 *
 * THE PROBLEM
 *
 * Stock behaviour eager-loaded 69 images (~5.7MB) of which one was above the fold, so
 * the hero — the LCP element — fought sixty-eight other downloads for bandwidth and the
 * page felt slow to appear (measured: FCP 5.0s, LCP 15.7s on throttled mobile).
 *
 * The first replacement was a lazy rolling window that only fetched images ~1800px ahead
 * of the viewport at low priority. Metrics looked good (LCP 5.6s) but it FELT worse:
 * on the slow connections most of this store's customers use, a scroll outruns an
 * 1800px window, so every card appeared as a blank box that then filled in. Waiting is
 * the one thing a slow connection makes expensive; that design maximised it.
 *
 * THE DESIGN
 *
 * On a bandwidth-limited connection, order matters far more than volume: fetching more
 * does not make any single image arrive sooner, it only decides WHICH images arrive
 * first. So load everything, in three strict tiers:
 *
 *   Tier 0 — critical. The hero / anything above the fold. Marked up as
 *            eager + fetchpriority=high in Liquid; this script never touches it and
 *            starts nothing until it has painted (LCP-gated, see below).
 *
 *   Tier 1 — everything a SCROLL can reveal: the first image of each product card and
 *            all section imagery, in document order — the order the customer meets
 *            them — at NORMAL priority with real concurrency. No viewport window: by
 *            the time a human has taken in the hero and begun scrolling, this tier
 *            should already be mostly down.
 *
 *   Tier 2 — everything that needs a CLICK or SWIPE: inactive tab panels and the closed
 *            mega menu. Low priority, and this script starts none of it until Tier 1 has
 *            fully drained. Fine to fetch — never fine to fetch ahead of something
 *            scrollable.
 *
 * WHAT THE TIERS CANNOT DO (learned the hard way — do not re-litigate)
 *
 * Tiering governs only the loads THIS SCRIPT starts. It has no authority over the ones
 * the browser starts by itself: `loading="lazy"` does not mean "wait", it means "fetch
 * when near the viewport" (~1250px on 4G). Anything the markup leaves fetchable and
 * geometrically near the fold will be downloaded whenever the browser feels like it,
 * whatever tier we assigned it.
 *
 * This bit us. Product cards used to render five slides each, laid out horizontally and
 * merely CLIPPED by `overflow: hidden` — visually hidden, geometrically on-screen. So the
 * browser happily fetched them. Measured on the live homepage: 48 swipe-only images began
 * downloading at ~2.5s while the eager/hero set did not finish until ~5.3s. They were
 * classified Tier 2 correctly and downloaded early anyway, competing with the LCP image —
 * exactly what the tiering exists to prevent.
 *
 * The fix was NOT more JavaScript. Withholding them (stashing `src`/`srcset` and restoring
 * later) was designed and deliberately rejected: it trades "wrong order" for "image might
 * never appear", and needs three separate restore paths to be safe. Instead the images
 * were removed at source — theme settings `show_second_image_on_hover` and
 * `product_card_carousel` are both OFF, which drives `max_visible_slide_count` to 1 in
 * `snippets/card-gallery.liquid`, so each card emits a single image and the ~192 extra
 * slides are never rendered at all. Deleting beats deferring.
 *
 * The rule this leaves behind: if a future change reintroduces imagery that is hidden by
 * clipping/opacity/transform rather than by `display: none`, this script CANNOT hold it
 * back. Either keep it out of the markup, or put it inside a container matched by
 * HIDDEN_CONTAINER_SELECTOR below (display:none subtrees are genuinely never fetched).
 *
 * The start signal is the Tier 0 images themselves settling — i.e. the hero has
 * actually finished — not `load` and not an LCP PerformanceObserver. Both were tried
 * and both start too early on a slow connection: `load` can fire while the hero is
 * still streaming, and LCP emits an early entry for the first text paint, goes quiet,
 * and only reports the hero much later — a quiet-gap heuristic on it kicked Tier 1 off
 * at ~2.8s against a hero that finished at ~9.4s, costing 4s of LCP under throttling.
 * Watching the eager images' own load events is deterministic. A hard timeout backstops
 * a hero that never settles.
 *
 * `saveData` is respected — prefetch nothing beyond what lazy loading fetches naturally.
 * There is deliberately NO 2g/3g tiering: slow is exactly when ordering matters most.
 */

/** Tier 1 parallelism. High enough to use the pipe, low enough not to saturate it. */
const TIER1_CONCURRENCY = 6;

/** Tier 2 parallelism. Background fill; politeness matters more than speed. */
const TIER2_CONCURRENCY = 3;

/** Start anyway if the hero has not settled by this long after DOMContentLoaded —
 *  a backstop for a broken hero image, generous because waiting for Tier 0 is the
 *  entire point of the gate. */
const HERO_TIMEOUT_MS = 15000;

/** Give the browser a beat after the hero settles before opening the floodgates. */
const POST_HERO_DELAY_MS = 200;

/**
 * Containers whose contents need a CLICK to be seen, so their images are Tier 2.
 *
 * This is a STRUCTURAL check — attribute/class selectors only — never a computed-style
 * one. The previous implementation used `checkVisibility()`, which resolves style per
 * element; called for every eager image at DOMContentLoaded (mid page-construction,
 * ~4,800 elements) and again for ~350 lazy images, it showed up as main-thread cost
 * inside the pre-LCP window on throttled mobile. `closest()` does pure selector
 * matching against the DOM tree and costs ~0.6ms for the whole page (vs ~20ms, and no
 * style/layout flush risk).
 *
 * The selector must name this theme's CSS-hidden containers explicitly (`.mega-menu`
 * and `.header-drawer` hide by class, not by any structural attribute). Verified
 * equivalent to the computed-style classifier across all 352 images then on the live
 * homepage — 0 tier disagreements. (That page now carries far fewer images; the
 * equivalence is a property of the selector, not of the count.) If a new
 * hidden-by-CSS-only container is added to the theme, it must be added here.
 *
 * Note this list only buys correct CLASSIFICATION. It also happens to buy real
 * withholding, but only incidentally: these containers hide via `display: none`, and the
 * browser never evaluates images in a display:none subtree for lazy loading. Containers
 * that hide by clipping or opacity get classified Tier 2 just the same, but the browser
 * will still fetch them whenever it likes — see "WHAT THE TIERS CANNOT DO" above.
 */
const HIDDEN_CONTAINER_SELECTOR =
  '[hidden], details:not([open]), dialog:not([open]), .mega-menu, .header-drawer';

/** @param {HTMLImageElement} image */
function isInHiddenContainer(image) {
  return Boolean(image.closest(HIDDEN_CONTAINER_SELECTOR));
}

/**
 * The first image of every gallery, collected once — a membership test replaces the
 * per-image `gallery.querySelector('img')` re-query, which used to re-run the identical
 * query once per slide (five times per gallery, across 64 galleries, when cards still
 * rendered five slides each).
 *
 * Still required even though cards now emit a single visible image: galleries continue to
 * contain per-variant images, which are rendered with the `hidden` attribute and so must
 * be told apart from the gallery's real first image.
 * @returns {WeakSet<Element>}
 */
function collectGalleryFirsts() {
  const firsts = new WeakSet();
  for (const gallery of document.querySelectorAll('.card-gallery, .slideshow-container')) {
    const first = gallery.querySelector('img');
    if (first) firsts.add(first);
  }
  return firsts;
}

/**
 * Begin an image and resolve when it settles. Flipping `loading` starts the fetch —
 * verified on this theme; images in `display:none` subtrees are never evaluated for
 * lazy loading otherwise.
 * @param {HTMLImageElement} image
 * @param {'auto' | 'low'} priority
 */
function load(image, priority) {
  return new Promise((resolve) => {
    if (image.dataset.ppcPrefetched || (image.complete && image.naturalWidth > 0)) {
      image.dataset.ppcPrefetched = '1';
      resolve();
      return;
    }
    image.dataset.ppcPrefetched = '1';

    const settle = () => {
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
      resolve();
    };
    image.addEventListener('load', settle);
    image.addEventListener('error', settle);

    if ('fetchPriority' in image) image.fetchPriority = priority;
    // Never let an arriving image block a frame: decode off the main thread. Matters
    // most for promoted (in-viewport) images landing mid-scroll on low-end devices.
    image.decoding = 'async';
    image.loading = 'eager';

    // An image that never settles must not wedge its worker.
    setTimeout(settle, 30000);
  });
}

/**
 * Drain a queue with n workers, preserving order.
 * @param {HTMLImageElement[]} queue
 * @param {number} concurrency
 * @param {'auto' | 'low'} priority
 */
async function drain(queue, concurrency, priority) {
  let cursor = 0;
  const worker = async () => {
    while (cursor < queue.length) {
      const image = queue[cursor];
      cursor += 1;
      await load(image, priority);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
}

function collectTiers() {
  /** @type {HTMLImageElement[]} */
  const tier1 = [];
  /** @type {HTMLImageElement[]} */
  const tier2 = [];

  const galleryFirsts = collectGalleryFirsts();

  // querySelectorAll returns document order — which IS scroll order.
  for (const image of document.querySelectorAll('img[loading="lazy"]')) {
    if (image.dataset.ppcPrefetched) continue;
    if (image.complete && image.naturalWidth > 0) continue;

    // A gallery image that is not its gallery's first is a swipe-only slide → Tier 2.
    const scrollReachable =
      !isInHiddenContainer(image) &&
      (galleryFirsts.has(image) || !image.closest('.card-gallery, .slideshow-container'));

    if (scrollReachable) tier1.push(image);
    else tier2.push(image);
  }
  return { tier1, tier2 };
}

/**
 * Queue-jumping. The drain fills the page top-to-bottom, but on a slow link it can
 * take ~10s+, and a customer who flicks straight down outruns its tail. So while the
 * drain runs, anything they actually APPROACH is promoted out of band at high
 * priority — the image in front of the customer always goes first, and the drain
 * simply skips it when its turn comes. Measured without this: scrolling at browsing
 * pace, only 76% of visible images were ready; the worst stop had 5 of 17.
 * @param {HTMLImageElement[]} images
 */
function promoteNearViewport(images) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        load(/** @type {HTMLImageElement} */ (entry.target), 'high');
      }
    },
    { rootMargin: '1500px' }
  );
  for (const image of images) observer.observe(image);
  return observer;
}

async function run() {
  const { tier1, tier2 } = collectTiers();
  const promoter = promoteNearViewport(tier1);
  await drain(tier1, TIER1_CONCURRENCY, 'auto');
  promoter.disconnect();
  // Tier 2 is click-gated content; it must never contend with anything. Wait for a
  // genuinely quiet moment rather than starting the instant Tier 1 drains.
  await new Promise((resolve) =>
    typeof requestIdleCallback === 'function'
      ? requestIdleCallback(resolve, { timeout: 4000 })
      : setTimeout(resolve, 1500)
  );
  await drain(tier2, TIER2_CONCURRENCY, 'low');
}

let started = false;
function start() {
  if (started) return;
  started = true;
  setTimeout(run, POST_HERO_DELAY_MS);
}

/** Wait for every Tier 0 image — anything the markup made eager — to settle.
 *  Runs at DOMContentLoaded, inside the pre-LCP window: the structural hidden-container
 *  check here (instead of a computed-style one) is what keeps this call free. */
function whenHeroSettled() {
  const critical = [...document.querySelectorAll('img:not([loading="lazy"])')].filter(
    (image) => !isInHiddenContainer(image) && !(image.complete && image.naturalWidth > 0)
  );
  if (critical.length === 0) {
    start();
    return;
  }
  let pending = critical.length;
  const settle = () => {
    pending -= 1;
    if (pending === 0) start();
  };
  for (const image of critical) {
    image.addEventListener('load', settle, { once: true });
    image.addEventListener('error', settle, { once: true });
  }
}

const saveData =
  (navigator.connection || navigator.mozConnection || navigator.webkitConnection)?.saveData;

if (!saveData) {
  if (document.readyState !== 'loading') {
    whenHeroSettled();
    setTimeout(start, HERO_TIMEOUT_MS);
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        whenHeroSettled();
        setTimeout(start, HERO_TIMEOUT_MS);
      },
      { once: true }
    );
  }
}
